/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { ILabelProps, Label } from '@fluentui/react/lib/Label';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown, LuisDropdownProps } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField, LuisTextFieldProps } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { FormValidators } from 'src/core/utils/formValidators';
import { routeTable } from 'src/core/utils/routeTable';
import { projectCreationContentLeftMargin } from 'src/customText/verticals/projectCreationModal/ProjectCreationModal';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { isDocumentSwaggerRtl } from 'src/languageStudio/core/utils/demosUtils';
import { qnaProjectNameBlacklistValidator } from 'src/qnaMaker/appShell/QnaMakerProjectsPage/QnaMakerProjectsPage';
import { QnaMakerLanguage } from 'src/qnaMaker/core/types/QnaMakerLanguages';
import { getProjectSupportedLanguageDropdownOptions } from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/components/QnaMakerChooseLanguageSettingScreen';
import { QnaMakerProjectModalFooter } from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/components/QnaMakerProjectModalFooter';
import {
    QnaMakerProjectCreationSteps,
    QnaMakerProjectDto
} from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/QnaMakerProjectCreationModal';
import {
    DefaultAnswerLocalizationType,
    getLanguageDefaultAnswer
} from 'src/qnaMaker/verticals/qnaMakerProjectCreationModal/utils/QnaMakerLanguageDefaultAnswer';

export type ProjectCreationModalBasicInfoProps = {
    onDismiss: () => void;
    onPrevious: () => void;
    onNext: (stepInput: Partial<QnaMakerProjectDto>) => void;
    projectDto: QnaMakerProjectDto;
};

const messages = defineMessages({
    title: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.title',
        defaultMessage: 'Enter basic information'
    },
    description: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.description',
        defaultMessage: 'Enter the basic information for your custom question answering knowledge base such as name and description.'
    },
    languageResourceLabel: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.languageResourceLabel',
        defaultMessage: 'Language resource'
    },
    azureSearchResourceLabel: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.azureSearchResourceLabel',
        defaultMessage: 'Azure search resource'
    },
    azureResourcePlaceholder: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.azureResourcePlaceholder',
        defaultMessage: 'No item selected'
    },
    changeAzureResource: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.changeAzureResource',
        defaultMessage: 'To change your resource go to '
    },
    azureSearch: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.azureSearch',
        defaultMessage: 'Azure Search'
    },
    nameLabel: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.nameLabel',
        defaultMessage: 'Name'
    },
    namePlaceholder: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.namePlaceholder',
        defaultMessage: 'Type project name'
    },
    descriptionLabel: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.descriptionLabel',
        defaultMessage: 'Description'
    },
    descriptionPlaceholder: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.descriptionPlaceholder',
        defaultMessage: 'Type project description'
    },
    languageLabel: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.languageLabel',
        defaultMessage: 'Source language'
    },
    languageTooltipText: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.languageTooltipText',
        defaultMessage:
            'This is the language that your sources will be in. If multiple languages are allowed in your resource, this is configurable for each project'
    },
    languagePlaceholder: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.languagePlaceholder',
        defaultMessage: 'Select the language of the text'
    },
    defaultAnswerLabel: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.defaultAnswerLabel',
        defaultMessage: 'Default answer when no answer is returned'
    },
    defaultAnswerTooltipText: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.defaultAnswerTooltipText',
        defaultMessage:
            'This is the default answer the system will send if there was no answer found for the question. You can change this at any time in Project settings'
    },
    settings: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.settings',
        defaultMessage: 'Settings'
    },
    duplicatedNameErrorMessage: {
        id: 'qnaMaker.qnaMakerEnterBasicInfoScreen.duplicatedNameErrorMessage',
        defaultMessage: 'This name already exists.'
    }
});

const { styleComponent, getClassName } = getStylistV2('ProjectCreationModalBasicInfo');
const StyledDropdown = styleComponent<LuisDropdownProps>(LuisDropdown)('StyledDropdown', { maxWidth: '315px' });
export const StyledTextField = styleComponent<LuisTextFieldProps>(LuisTextField)('StyledTextField', { maxWidth: '315px' });
const LabelContainer = styleComponent<ILabelProps>(Label)('LabelContainer', { display: 'flex' });

const calloutIconClassName = getClassName('calloutIconClassName', {
    fontSize: '14px',
    lineHeight: '14px'
});
const fieldsMinWidth = '315px';

export const QnaMakerEnterBasicInfoScreen = observer((props: ProjectCreationModalBasicInfoProps) => {
    const { onDismiss, onPrevious, onNext, projectDto } = props;
    const history = useHistory();
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const { csAccountStore: languageResource } = rootStore.azureStore.session;
    const { qnaProjectStoreHouse } = rootStore;

    const { formState, formStateDto, updateFormState } = useFormState<QnaMakerProjectDto>({
        multilingualResource: [projectDto?.multilingualResource],
        name: [projectDto?.name, qnaProjectNameBlacklistValidator, FormValidators.createRequiredValidator()],
        description: [projectDto?.description],
        language: [projectDto?.language, FormValidators.createRequiredValidator()],
        defaultAnswer: [
            projectDto?.language ? getLanguageDefaultAnswer(projectDto.language) : getLanguageDefaultAnswer('en'),
            FormValidators.createRequiredValidator()
        ]
    });

    const duplicatedProjName = qnaProjectStoreHouse.items.find(p => p.name.toLowerCase() === formState.fields.name.value.toLowerCase());

    const languageResourcesOptions: IDropdownOption[] =
        (languageResource && [{ key: languageResource.id, text: languageResource.name }]) || [];
    const azureSearchOptions: IDropdownOption[] =
        (languageResource && [
            { key: languageResource.ownedSearchResourceInfo.name, text: languageResource.ownedSearchResourceInfo.name }
        ]) ||
        [];
    const languagesOptions: IDropdownOption[] = getProjectSupportedLanguageDropdownOptions(localize);

    const isAuthoringResourceSelected = Boolean(languageResource);

    const isModalValid =
        !duplicatedProjName &&
        formState.form.valid &&
        Boolean(formStateDto.language) &&
        Boolean(languageResource) &&
        Boolean(languageResource.ownedSearchResourceInfo.name);

    const labelOnRender = (label: string, tooltipText: string | React.ReactNode, isRequired: boolean = true) => (
        <Stack horizontal verticalAlign="center" grow>
            <LabelContainer styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }} required={isRequired}>
                {label}
                &nbsp;
            </LabelContainer>
            <CalloutButton content={tooltipText} iconClassName={calloutIconClassName} />
        </Stack>
    );

    const azureSearchFeatureToggleLinkProps = () => ({ onClick: () => history.push(routeTable.qnaMaker.azureSearchPage()) });

    return (
        <Stack>
            <Stack
                tokens={{ childrenGap: 24, padding: `0 0 0 ${projectCreationContentLeftMargin}` }}
                styles={{ root: { maxHeight: '500px', overflowY: 'auto' } }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>{localize(messages.description)}</LuisText>
                </Stack>

                <Stack styles={{ root: { maxHeight: '426px', overflowY: 'auto', paddingBottom: '8px' } }} tokens={{ childrenGap: 8 }}>
                    <Stack.Item>
                        <StyledDropdown
                            disabled
                            options={languageResourcesOptions}
                            selectedKey={languageResource?.id}
                            styles={{ root: { minWidth: fieldsMinWidth } }}
                            label={localize(messages.languageResourceLabel)}
                            placeholder={localize(messages.azureResourcePlaceholder)}
                        />
                        <LuisText variant="small">
                            {localize(messages.changeAzureResource)}
                            <Link onClick={() => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })}>
                                {localize(messages.settings)}
                            </Link>
                        </LuisText>
                    </Stack.Item>

                    <Stack.Item>
                        <StyledDropdown
                            disabled
                            options={azureSearchOptions}
                            styles={{ root: { minWidth: fieldsMinWidth } }}
                            label={localize(messages.azureSearchResourceLabel)}
                            placeholder={localize(messages.azureResourcePlaceholder)}
                            selectedKey={languageResource?.ownedSearchResourceInfo.name}
                        />
                        <LuisText variant="small">
                            {localize(messages.changeAzureResource)}
                            <ExternalLink as="a" {...azureSearchFeatureToggleLinkProps()}>
                                {localize(messages.azureSearch)}
                            </ExternalLink>
                        </LuisText>
                    </Stack.Item>

                    <StyledTextField
                        required
                        maxLength={100}
                        data-automation-id="projectName"
                        value={formState.fields.name.value}
                        label={localize(messages.nameLabel)}
                        styles={{ root: { minWidth: fieldsMinWidth } }}
                        placeholder={localize(messages.namePlaceholder)}
                        onChange={(_, val) => updateFormState({ name: val })}
                        errorMessage={
                            duplicatedProjName ? localize(messages.duplicatedNameErrorMessage) : formState.fields.name.errorMessage
                        }
                    />

                    <StyledTextField
                        disabled={!isAuthoringResourceSelected}
                        data-automation-id="projectDescription"
                        value={formState.fields.description.value}
                        label={localize(messages.descriptionLabel)}
                        styles={{ root: { minWidth: fieldsMinWidth } }}
                        placeholder={localize(messages.descriptionPlaceholder)}
                        onChange={(_, val) => updateFormState({ description: val })}
                    />

                    <StyledDropdown
                        maxHeight={200}
                        data-automation-id="projectLanguagesOptions"
                        orderByKey="none"
                        options={languagesOptions}
                        disabled={!projectDto.multilingualResource}
                        selectedKey={formState.fields.language.value}
                        styles={{ root: { minWidth: fieldsMinWidth } }}
                        placeholder={localize(messages.languagePlaceholder)}
                        onChange={(_, o) =>
                            updateFormState({
                                language: o.key as QnaMakerLanguage,
                                defaultAnswer: getLanguageDefaultAnswer(o.key as DefaultAnswerLocalizationType)
                            })
                        }
                        onRenderLabel={() => labelOnRender(localize(messages.languageLabel), localize(messages.languageTooltipText))}
                    />

                    <StyledTextField
                        maxLength={100}
                        value={formState.fields.defaultAnswer.value}
                        onChange={(_, val) => updateFormState({ defaultAnswer: val })}
                        dir={isDocumentSwaggerRtl(formState.fields.language.value as Language['code']) ? 'rtl' : 'ltr'}
                        onRenderLabel={() =>
                            labelOnRender(localize(messages.defaultAnswerLabel), localize(messages.defaultAnswerTooltipText))
                        }
                    />
                </Stack>
            </Stack>

            <QnaMakerProjectModalFooter
                isValid={isModalValid}
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                onNext={() => onNext(formStateDto)}
                activeStep={QnaMakerProjectCreationSteps.EnterBasicInformation}
            />
        </Stack>
    );
});
